import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild, inject } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { debounceTime } from 'rxjs';

import { InputTitleComponent } from '@atoms/input-title/input-title.component';
import { SsrService } from '@services/ssr.service';

@Component({
  selector: 'app-range-slider',
  imports: [NgClass, NgStyle, InputTitleComponent],
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
})
export class RangeSliderComponent implements OnChanges {
  private ssrService = inject(SsrService);
  @Input() color: 'default' | 'dark' = 'default';
  @Input() min?: number;
  @Input('min-label') minLabel?: string | number;
  @Input() max?: number;
  @Input('max-label') maxLabel?: string | number;
  @Input('label-size') labelSize?: 'small' | 'medium' = 'medium';
  @Input() steps?: number[];
  @Input() value: any[] = [this.min, this.max];
  @Input() disabled: boolean = false;
  @Input() thousands: boolean = false;
  @Input() price: boolean = false;
  @Input() unit?: string;
  @Input() label?: string;
  @Input('disabled-from') disabledFrom?: boolean;
  @Input('disabled-to') disabledTo?: boolean;
  @Input() stepsLabel: boolean = false;
  @Output() valueChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() updated: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() dragstart: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() dragend: EventEmitter<number[]> = new EventEmitter<number[]>();
  @ViewChild('rangeSlider') rangeSlider?: ElementRef;
  public draged: boolean = false;
  public position: number[] = [0, 100];
  public stepsPercentage?: number[];
  public movingDot: number = 0;
  public animate: boolean = false;
  public animateTimeout?: ReturnType<typeof setTimeout>;
  public valueFormated: number[] = [];

  mousedown(event: MouseEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    this.ssrService.getWindow()?.getSelection()?.removeAllRanges();
    if (this.animateTimeout) {
      clearTimeout(this.animateTimeout);
      this.animate = false;
    }
    const element: HTMLElement | null = event.target as HTMLElement;
    if (element.closest('.range-slider__dot') !== null) return;
    this.animate = true;
  }

  mouseup(event: MouseEvent): void {
    if (this.disabled) return;
    const element: HTMLElement | null = event.target as HTMLElement;
    if (element.closest('.range-slider__dot') !== null) return;
    this.animateTimeout = setTimeout(() => {
      this.animate = false;
    }, 250);
  }

  click(event: MouseEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    const element: HTMLElement | null = event.target as HTMLElement;
    if (element.closest('.range-slider__dot') !== null) return;
    const rangeSlider: HTMLElement | null = this.rangeSlider?.nativeElement;
    const rangeSliderWidth: number = rangeSlider?.offsetWidth ?? 0;
    const rangeSliderLeft: number = rangeSlider?.getBoundingClientRect().left ?? 0;
    const clickPosition: number = event.clientX - rangeSliderLeft;
    let newPosition: number = (clickPosition / rangeSliderWidth) * 100;
    if (this.stepsPercentage?.length) {
      const closerStep: number = this.getCloserStep(newPosition, this.stepsPercentage);
      newPosition = closerStep;
    }
    let index: number = Math.abs(this.position[0] - newPosition) < Math.abs(this.position[1] - newPosition) ? 0 : 1;
    if (this.disabledFrom) index = 1;
    else if (this.disabledTo) index = 0;
    this.position[index] = newPosition;
    this.setValue(index, newPosition);
    this.valueChange.emit(this.valueFormated);
  }

  getCloserStep(value: number, array?: number[]): number {
    if (!array) return value;
    let closerStep: number = array[0];
    array?.forEach((step) => {
      if (Math.abs(step - value) < Math.abs(closerStep - value)) closerStep = step;
    });
    return closerStep;
  }

  dragStart(type: 'min' | 'max', event: any): void {
    if (this.disabled) return;
    event.preventDefault();
    const index: number = type === 'min' ? 0 : 1;
    this.movingDot = this.position[index];
    this.draged = true;
    this.dragstart.emit(this.valueFormated);
  }

  dragging(type: 'min' | 'max', event: any): void {
    if (this.disabled || (event.direction !== 2 && event.direction !== 4)) return;
    event.preventDefault();
    const index: number = type === 'min' ? 0 : 1;
    const rangeSlider: HTMLElement | null = this.rangeSlider?.nativeElement;
    const rangeSliderWidth: number = rangeSlider?.offsetWidth ?? 0;

    const deltaX: number = event.deltaX < 0 ? -event.deltaX : event.deltaX;
    let newPosition: number = (deltaX / rangeSliderWidth) * 100;
    newPosition = event.deltaX < 0 ? -newPosition : newPosition;
    newPosition += this.movingDot;
    if (newPosition < 0) newPosition = 0;
    else if (newPosition > 100) newPosition = 100;

    if (index === 0 && newPosition > this.position[1]) newPosition = this.position[1];
    else if (index === 1 && newPosition < this.position[0]) newPosition = this.position[0];

    if (this.stepsPercentage?.length) {
      const closerStep: number = this.getCloserStep(newPosition, this.stepsPercentage);
      newPosition = closerStep;
    }

    this.position[index] = newPosition;
    this.setValue(index, newPosition);
    this.valueChange.emit(this.valueFormated);
  }

  dragEnd(): void {
    if (this.disabled) return;
    this.draged = false;
    this.dragend.emit(this.valueFormated);
  }

  setValue(index: number, percentage: number): void {
    if (this.min === undefined || this.max === undefined) return;
    this.valueFormated[index] = this.getCloserStep(Math.round((percentage / 100) * (this.max - this.min) + this.min), this.steps);
  }

  initValues(): void {
    if (this.min === undefined) {
      if (this.steps?.length) this.min = this.steps[0];
      else this.min = 0;
    }

    if (this.max === undefined) {
      if (this.steps?.length) this.max = this.steps[this.steps.length - 1];
      else this.max = 100;
    }

    let min: number = this.min;
    let max: number = this.max;
    if (this.value[0] !== undefined) min = typeof this.value[0] === 'number' ? this.value[0] : Number(this.value[0]);
    if (this.value[1] !== undefined) max = typeof this.value[1] === 'number' ? this.value[1] : Number(this.value[1]);

    this.valueFormated = [min, max];

    let minPosition: number = ((this.valueFormated[0] - this.min) / (this.max - this.min)) * 100;
    if (minPosition < 0) minPosition = 0;
    else if (minPosition > 100) minPosition = 100;
    let maxPosition: number = ((this.valueFormated[1] - this.min) / (this.max - this.min)) * 100;
    if (maxPosition > 100) maxPosition = 100;
    else if (maxPosition < 0) maxPosition = 0;
    this.position = [minPosition, maxPosition];

    const minNumber: number = this.min;

    if (this.steps?.length) {
      const stepDiff: number = this.steps[this.steps.length - 1] - this.steps[0];
      this.stepsPercentage = this.steps?.map((step) => ((step - minNumber) / stepDiff) * 100) ?? [];
    }
  }

  constructor() {
    this.valueChange.pipe(debounceTime(500), takeUntilDestroyed()).subscribe((value) => {
      this.updated.emit(value);
    });
  }

  ngOnChanges(): void {
    this.initValues();
  }
}
